<template>
  <div class="card text-[var(--color-black)] w-full" data-testid="knowledge-database">
    <Accordion value="0" class="min-h-full box-border w-full mb-8 mx-auto" data-testid="kd-accordion">
        <AccordionPanel v-for="(item, index) in getKnowledgeDatabase" :key="index"
            :value="index"
            :data-testid="`kd-panel-${index}`">
            <AccordionHeader>
              <span data-testid="kd-panel-title">{{ item.title[getLocale] }}</span>
            </AccordionHeader>
            <AccordionContent>
                <div
                  v-html="cleanContent(item.pageContent[getLocale])"
                  class="knowledge-content break-words"
                  data-testid="kd-panel-content"
                ></div>
                <!-- Media Gallery -->
                <div
                  v-if="item.media && item.media.length > 0"
                  class="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
                  data-testid="kd-media-gallery"
                >
                  <div
                    v-for="(media, mediaIndex) in item.media"
                    :key="mediaIndex"
                    class="relative rounded-lg overflow-hidden border border-gray-200"
                    :data-testid="`kd-media-${mediaIndex}`"
                  >
                    <!-- Image -->
                    <div v-if="media.type === 'image'"
                     class="aspect-video bg-gray-100 group cursor-pointer">
                      <img
                        :src="media.url"
                        :alt="media.alt || 'Article image'"
                        class="w-full h-full object-cover transition-transform
                         duration-300 group-hover:scale-105"
                        data-testid="kd-media-image"
                        @error="handleImageError"
                        @click="openImageModal(media.url)"
                      />
                    </div>
                    <!-- Video -->
                    <div
                      v-else-if="media.type === 'video'"
                      class="aspect-video bg-gray-900 relative"
                    >
                      <!-- eslint-disable-next-line vuejs-accessibility/media-has-caption -->
                      <video
                        :src="media.url"
                        controls
                        class="w-full h-full"
                        data-testid="kd-media-video"
                        @error="handleVideoError"
                      >
                        Your browser does not support the video tag.
                      </video>
                    </div>
                  </div>
                </div>
            </AccordionContent>
        </AccordionPanel>
    </Accordion>
  </div>
  <provide-feedback-component></provide-feedback-component>

  <!-- Image Modal -->
  <Dialog
    v-model:visible="imageModalVisible"
    modal
    :style="{ width: '90vw', maxWidth: '1200px' }"
    data-testid="kd-image-modal"
    @hide="closeImageModal"
  >
    <template #header>
      <span class="text-xl font-semibold">Image Preview</span>
    </template>
    <div class="flex justify-center items-center">
      <img
        :src="imageModalUrl"
        alt="Preview"
        class="max-w-full max-h-[80vh] object-contain"
      />
    </div>
  </Dialog>
</template>

<script>
import ProvideFeedbackComponent from '@/components/global/ProvideFeedbackComponent.vue';
import { sanitizeHiddenChars } from '@/helpers/sanitizeText';
import Accordion from 'primevue/accordion';
import AccordionPanel from 'primevue/accordionpanel';
import AccordionHeader from 'primevue/accordionheader';
import AccordionContent from 'primevue/accordioncontent';
import Dialog from 'primevue/dialog';

export default {
  name: 'KnowledgeDatabaseComponent',
  components: {
    ProvideFeedbackComponent,
    Accordion,
    AccordionPanel,
    AccordionHeader,
    AccordionContent,
    Dialog,
  },
  data() {
    return {
      isOMINDExpanded: false,
      isHUGOExpanded: false,
      isRequirementsExpanded: false,
      imageModalVisible: false,
      imageModalUrl: '',
    };
  },
  computed: {
    getKnowledgeDatabase() {
      return this.$store.getters.getKnowledgeDatabaseContent;
    },
    getLocale() {
      return this.$i18n.locale;
    },
  },
  async mounted() {
    await this.$store.dispatch('getKnowledgeDatabase');
  },
  unmounted() {
    this.$store.commit('setKnowledgeDatabaseContent', []);
  },
  methods: {
    cleanContent(html) {
      return sanitizeHiddenChars(html);
    },
    handleImageError(event) {
      console.error('Failed to load image:', event.target.src);
      const imgElement = event.target;
      imgElement.style.display = 'none';
    },
    handleVideoError(event) {
      console.error('Failed to load video:', event.target.src);
      const videoElement = event.target;
      videoElement.style.display = 'none';
    },
    openImageModal(url) {
      this.imageModalUrl = url;
      this.imageModalVisible = true;
    },
    closeImageModal() {
      this.imageModalVisible = false;
      this.imageModalUrl = '';
    },
  },
};
</script>
<style scoped>
.p-accordionheader{
    @apply text-2xl;
    word-wrap: break-word;
    word-break: break-word;
    overflow-wrap: break-word;
    max-width: 100%;
}
.p-accordionpanel:not(.p-accordionpanel-active):not(.p-disabled) > .p-accordionheader:hover {
    @apply text-[var(--color-orange)];
}

:deep(h3){
    @apply text-2xl font-medium my-2;
}

:deep(p){
    @apply text-lg mb-2;
    word-wrap: break-word;
    word-break: break-word;
    overflow-wrap: break-word;
    max-width: 100%;
}

:deep(ol), :deep(ul){
    @apply pl-8 my-2 text-lg;
    word-wrap: break-word;
    word-break: break-word;
    overflow-wrap: break-word;
    max-width: 100%;
}

.knowledge-content {
    word-wrap: break-word;
    word-break: break-word;
    overflow-wrap: break-word;
    max-width: 100%;
}

.knowledge-content :deep(*) {
    word-wrap: break-word;
    word-break: break-word;
    overflow-wrap: break-word;
    max-width: 100%;
}

/* Media Gallery Styles */
.media-gallery {
    @apply mt-6;
}

.media-item {
    @apply transition-all duration-300;
}

.media-item:hover {
    @apply shadow-lg;
}
</style>
